import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";

import { format } from "date-fns";
import { Space, Breadcrumb, Card, Typography, Table, Tag, Spin, Empty, Button, Row, Col, Statistic } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { 
  WifiOutlined, 
  ClockCircleOutlined, 
  ReloadOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  EnvironmentOutlined
} from "@ant-design/icons";

import MqttDeviceStore, { MqttDeviceData } from "../../stores/MqttDeviceStore";
import { useTitle } from "../helpers";

interface DeviceDataEntry {
  key: string;
  timestamp: string;
  data: any;
  formattedData: string;
  validData: boolean;
}

function MqttDeviceDetails() {
  const { serial } = useParams<{ serial: string }>();
  const [latestData, setLatestData] = useState<MqttDeviceData | null>(null);
  const [historicalData, setHistoricalData] = useState<MqttDeviceData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);

  useTitle("MQTT Device Details", serial || "Unknown Device");

  useEffect(() => {
    if (serial) {
      loadDeviceData();
    }
  }, [serial]);

  const loadDeviceData = async () => {
    if (!serial) return;
    
    setLoading(true);
    
    try {
      // Load latest data
      await MqttDeviceStore.getLatestDataBySerial(serial, (data) => {
        console.log('DeviceDetails - Latest data received:', data);
        console.log('DeviceDetails - Data active field:', data?.active);
        console.log('DeviceDetails - Data validData field:', data?.validData);
        setLatestData(data);
      });

      // Load historical data
      await MqttDeviceStore.getDataBySerial(serial, 50, (data) => {
        console.log('DeviceDetails - Historical data received:', data);
        console.log('DeviceDetails - Historical data length:', data.length);
        if (data.length > 0) {
          console.log('DeviceDetails - First historical record active:', data[0]?.active);
        }
        setHistoricalData(data);
        setLoading(false);
      });
    } catch (error) {
      console.error('Error loading device data:', error);
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (!serial) return;
    
    setRefreshing(true);
    await loadDeviceData();
    setRefreshing(false);
  };

  const getDeviceStatus = () => {
    if (!latestData) return { text: "Offline", type: "danger" as const, icon: <CloseCircleOutlined /> };
    
    // Check if device is inactive first
    if (!latestData.active) {
      return { text: "Inactive", type: "default" as const, icon: <ExclamationCircleOutlined /> };
    }
    
    const now = Math.floor(Date.now() / 1000);
    const timeDiff = now - latestData.lastReadingAt;
    
    if (timeDiff < 86400) { // 24 hours
      // Check if most recent data is valid
      if (latestData.validData) {
        return { text: "Online - Success", type: "success" as const, icon: <CheckCircleOutlined /> };
      } else {
        return { text: "Online - Invalid", type: "warning" as const, icon: <ExclamationCircleOutlined /> };
      }
    } else {
      return { text: "Offline", type: "danger" as const, icon: <CloseCircleOutlined /> };
    }
  };

  const parseJsonData = (jsonData: string) => {
    console.log('DeviceDetails - Parsing JSON data:', jsonData);
    try {
      const parsed = JSON.parse(jsonData);
      console.log('DeviceDetails - Parsed JSON data:', parsed);
      return parsed;
    } catch (error) {
      console.error('DeviceDetails - Error parsing JSON data:', error, 'Raw data:', jsonData);
      return null;
    }
  };

  const formatDataForDisplay = (data: any) => {
    if (!data) return "Invalid data";
    
    const entries = Object.entries(data);
    return entries.map(([key, value]) => {
      if (typeof value === 'object' && value !== null) {
        return `${key}: ${JSON.stringify(value)}`;
      }
      return `${key}: ${value}`;
    }).join(', ');
  };

  const getDataTableColumns = () => [
    {
      title: "Timestamp",
      dataIndex: "timestamp",
      key: "timestamp",
      width: 200,
      render: (timestamp: string) => (
        <Space>
          <ClockCircleOutlined />
          {timestamp}
        </Space>
      ),
    },
    {
      title: "Valid Data",
      dataIndex: "validData",
      key: "validData",
      width: 120,
      render: (validData: boolean) => (
        <Tag 
          color={validData ? 'success' : 'error'} 
          icon={validData ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
        >
          {validData ? 'Valid' : 'Invalid'}
        </Tag>
      ),
    },
    {
      title: "Data",
      dataIndex: "formattedData",
      key: "formattedData",
      render: (formattedData: string) => (
        <Typography.Text code style={{ fontSize: 12 }}>
          {formattedData}
        </Typography.Text>
      ),
    },
  ];

  const getDataTableData = (): DeviceDataEntry[] => {
    return historicalData.map((item, index) => {
      const parsedData = parseJsonData(item.jsonData);
      return {
        key: index.toString(),
        timestamp: format(new Date(item.lastReadingAt * 1000), "yyyy-MM-dd HH:mm:ss"),
        data: parsedData,
        formattedData: formatDataForDisplay(parsedData),
        validData: item.validData,
      };
    });
  };

  const getLatestDataStats = () => {
    if (!latestData) return null;
    
    const parsedData = parseJsonData(latestData.jsonData);
    if (!parsedData) return null;

    const stats = [];
    
    // Common sensor data fields
    if (parsedData.temperature !== undefined) {
      stats.push({
        title: "Temperature",
        value: `${parsedData.temperature}°C`,
        icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />
      });
    }
    
    if (parsedData.humidity !== undefined) {
      stats.push({
        title: "Humidity",
        value: `${parsedData.humidity}%`,
        icon: <CheckCircleOutlined style={{ color: '#1890ff' }} />
      });
    }
    
    if (parsedData.pressure !== undefined) {
      stats.push({
        title: "Pressure",
        value: `${parsedData.pressure} hPa`,
        icon: <CheckCircleOutlined style={{ color: '#722ed1' }} />
      });
    }
    
    if (parsedData.battery_level !== undefined) {
      stats.push({
        title: "Battery",
        value: `${parsedData.battery_level}%`,
        icon: <CheckCircleOutlined style={{ color: parsedData.battery_level > 20 ? '#52c41a' : '#faad14' }} />
      });
    }

    return stats;
  };

  // Helper function to recursively search for latitude and longitude in nested objects
  const findLatLngRecursively = (obj: any, path: string = ''): { lat: any, lng: any, path: string } | null => {
    if (obj === null || obj === undefined) return null;
    
    // Check if current object has latitude and longitude
    if (obj.latitude !== undefined && obj.longitude !== undefined) {
      return { lat: obj.latitude, lng: obj.longitude, path };
    }
    
    // If it's an object, recursively search its properties
    if (typeof obj === 'object' && !Array.isArray(obj)) {
      for (const [key, value] of Object.entries(obj)) {
        const currentPath = path ? `${path}.${key}` : key;
        const result = findLatLngRecursively(value, currentPath);
        if (result) return result;
      }
    }
    
    // If it's an array, search each element
    if (Array.isArray(obj)) {
      for (let i = 0; i < obj.length; i++) {
        const currentPath = `${path}[${i}]`;
        const result = findLatLngRecursively(obj[i], currentPath);
        if (result) return result;
      }
    }
    
    return null;
  };

  const getDeviceLocation = () => {
    if (!latestData) return "Unknown";
    
    const parsedData = parseJsonData(latestData.jsonData);
    if (!parsedData) return "Unknown";
    
    // Recursively search for latitude and longitude
    const location = findLatLngRecursively(parsedData);
    
    if (location) {
      return `${location.lat}, ${location.lng}`;
    }
    
    return "Unknown";
  };

  if (loading) {
    return (
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        <PageHeader
          title={
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <div style={{ fontSize: '20px', fontWeight: '600', lineHeight: '1.2' }}>Device: {serial}</div>
              <div style={{ fontSize: '14px', fontWeight: 'normal', color: '#666', marginTop: '4px', lineHeight: '1.4' }}>
                Device details and data history
              </div>
            </div>
          }
        />
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Typography.Text>Loading device data...</Typography.Text>
          </div>
        </div>
      </Space>
    );
  }

  if (!latestData && historicalData.length === 0) {
    return (
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        <PageHeader
          title={
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <div style={{ fontSize: '20px', fontWeight: '600', lineHeight: '1.2' }}>Device: {serial}</div>
              <div style={{ fontSize: '14px', fontWeight: 'normal', color: '#666', marginTop: '4px', lineHeight: '1.4' }}>
                Device details and data history
              </div>
            </div>
          }
        />
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span>
              No data found for device "{serial}". The device may not have sent any data yet.
            </span>
          }
        />
      </Space>
    );
  }

  const status = getDeviceStatus();
  const latestStats = getLatestDataStats();

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      <PageHeader
        title={`Device: ${serial}`}
        subTitle="Device details and data history"
        extra={[
          <Button key="back" icon={<ArrowLeftOutlined />}>
            <Link to="/mqtt-devices">Back to Devices</Link>
          </Button>,
          <Button 
            key="refresh" 
            icon={<ReloadOutlined />} 
            loading={refreshing}
            onClick={handleRefresh}
          >
            Refresh
          </Button>,
          <Button 
            key="mark-status" 
            type="default"
            onClick={async () => {
              if (!serial) return;
              
              try {
                // Check both latest data and historical data for active status
                let isCurrentlyActive = latestData?.active ?? false;
                
                // If no latest data, check historical data for active status
                if (!latestData && historicalData.length > 0) {
                  // Use the most recent historical record's active status
                  isCurrentlyActive = historicalData[0]?.active ?? false;
                }
                
                console.log('Button click - latestData:', latestData);
                console.log('Button click - historicalData length:', historicalData.length);
                console.log('Button click - latestData active:', latestData?.active);
                console.log('Button click - historicalData[0] active:', historicalData[0]?.active);
                console.log('Button click - final isCurrentlyActive:', isCurrentlyActive);
                let success = false;
                
                if (isCurrentlyActive) {
                  // Device is currently active, mark as inactive
                  success = await MqttDeviceStore.markDeviceInactive(serial);
                } else {
                  // Device is currently inactive, mark as active
                  success = await MqttDeviceStore.markDeviceActive(serial);
                }
                
                if (success) {
                  // Refresh the device data to reflect the change
                  await loadDeviceData();
                }
              } catch (error) {
                console.error('Error changing device status:', error);
              }
            }}
          >
{(() => {
              // Use the same logic as getDeviceStatus() to determine button text
              let buttonText = 'Mark Active'; // Default for devices with no data
              
              if (latestData) {
                // Device has data - check active status
                if (latestData.active) {
                  buttonText = 'Mark Inactive';
                } else {
                  buttonText = 'Mark Active';
                }
              } else if (historicalData.length > 0) {
                // No latest data but has historical data - check historical active status
                if (historicalData[0]?.active) {
                  buttonText = 'Mark Inactive';
                } else {
                  buttonText = 'Mark Active';
                }
              }
              
              console.log('Button text render - latestData:', latestData);
              console.log('Button text render - latestData active:', latestData?.active);
              console.log('Button text render - historicalData[0] active:', historicalData[0]?.active);
              console.log('Button text render - final buttonText:', buttonText);
              return buttonText;
            })()}
          </Button>
        ]}
      />

      {/* Device Status Card */}
      <Card title="Device Status">
        <Row gutter={16}>
          <Col span={8}>
            <Statistic
              title="Status"
              value={status.text}
              prefix={status.icon}
              valueStyle={{ color: status.type === 'success' ? '#52c41a' : status.type === 'warning' ? '#faad14' : status.type === 'default' ? '#8c8c8c' : '#ff4d4f' }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Location"
              value={getDeviceLocation()}
              prefix={<EnvironmentOutlined />}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Last Seen"
              value={latestData ? format(new Date(latestData.lastReadingAt * 1000), "MMM dd, HH:mm") : "Never"}
              prefix={<ClockCircleOutlined />}
            />
          </Col>
        </Row>
        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col span={8}>
            <Statistic
              title="Total Records"
              value={historicalData.length}
              prefix={<WifiOutlined />}
            />
          </Col>
        </Row>
      </Card>

      {/* Latest Data Stats */}
      {latestStats && latestStats.length > 0 && (
        <Card title="Latest Sensor Data">
          <Row gutter={16}>
            {latestStats.map((stat, index) => (
              <Col span={6} key={index}>
                <Statistic
                  title={stat.title}
                  value={stat.value}
                  prefix={stat.icon}
                />
              </Col>
            ))}
          </Row>
        </Card>
      )}

      {/* Latest Data Details */}
      {latestData && (
        <Card title="Latest Data Details">
          <Typography.Paragraph>
            <Typography.Text strong>Timestamp: </Typography.Text>
            {format(new Date(latestData.lastReadingAt * 1000), "yyyy-MM-dd HH:mm:ss")}
          </Typography.Paragraph>
          <Typography.Paragraph>
            <Typography.Text strong>Raw Data: </Typography.Text>
            <Typography.Text code style={{ display: 'block', marginTop: 8, whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(parseJsonData(latestData.jsonData), null, 2)}
            </Typography.Text>
          </Typography.Paragraph>
        </Card>
      )}

      {/* Historical Data Table */}
      <Card title="Data History">
        <Table
          columns={getDataTableColumns()}
          dataSource={getDataTableData()}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
          }}
          scroll={{ x: true }}
        />
      </Card>
    </Space>
  );
}

export default MqttDeviceDetails;